/*
 * Reading the reader-facing pages' filter state back out of the query string.
 *
 * The browse page has kept its filters in the URL from the start - linkable,
 * bookmarkable, and the back button does what it looks like it should - and the
 * release-note list (S42, 2026-10-12) keeps its product filter and page the same
 * way, under the same names: `?products=1,2&page=3`. So the two readers of that
 * spelling live here, once, rather than as two private copies that could start to
 * disagree about what `?products=1,,x,2` means.
 *
 * Both are forgiving on purpose. A URL is typed, pasted and edited by hand, and a
 * malformed part is dropped rather than refused: the page shows what it can make
 * of the address instead of an error about it. The server normalises the same
 * values again and echoes them back, and that echo - not these - is what a page
 * lays itself out by.
 */

/**
 * The ids in a comma-separated list such as `?products=1,2`: positive whole
 * numbers, in the order written. Anything else - blanks, words, zero, negatives -
 * is dropped. An absent or empty parameter is the empty list.
 */
export function parseIds(value: string | null): number[] {
  if (!value) return [];
  return value
    .split(',')
    .map((part) => Number.parseInt(part.trim(), 10))
    .filter((id) => Number.isInteger(id) && id > 0);
}

/** The `?page=` number: a whole number of at least 1, and 1 for anything else. */
export function parsePage(value: string | null): number {
  return Math.max(1, Number.parseInt(value ?? '1', 10) || 1);
}
